import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ChevronDown, History } from "lucide-react";

// P6L — unica sezione CHIUSA con tutta la cronologia tecnica: run
// precedenti (P1.7 legacy, HARD-654, HARD2), recovery consumate, marker
// diagnostici storici, hash. Solo consultazione: nessun pulsante attivo,
// nessun invito a riprovare.
const Row = ({ label, children }) => (
  <div className="flex flex-wrap gap-x-2 text-[10px]"><span className="text-muted-foreground">{label}</span><span className="break-anywhere">{children}</span></div>
);

const RunHistoryEntry = ({ r }) => (
  <details className="border border-border/70" data-testid={`tech-history-run-${r.run_id}`}>
    <summary className="touch-target flex items-center justify-between gap-2 px-2 cursor-pointer select-none text-[11px] list-none bg-white">
      <span className="min-w-0 break-anywhere"><span className="font-mono">{r.run_id}</span> · <b>{r.status}</b>{r.superseded && <span className="ml-1 text-[9px] bg-slate-200 px-1">SUPERSEDED</span>}</span>
      <ChevronDown size={12} className="shrink-0" />
    </summary>
    <div className="p-2 space-y-1">
      <Row label="Versione">{r.reader_version}</Row>
      {r.incomplete_reason && <Row label="Motivo">{r.incomplete_reason}</Row>}
      <Row label="Costo confermato / tetto">${r.real_cost_confirmed_usd ?? 0} / ${r.authorized_cap_usd ?? "—"}</Row>
      <div className="table-scroll border border-border">
        <table className="w-full text-[10px]">
          <thead><tr className="bg-muted text-left">{["Seg", "Stato", "POST", "GET", "Costo", "response_id", "Motivo"].map((h) => <th key={h} className="px-1 py-0.5 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>{(r.segments || []).map((s) => (
            <tr key={s.segment_id} className="border-t border-border/60">
              <td className="px-1 py-0.5 font-bold">{s.segment_id}</td><td className="px-1 py-0.5 whitespace-nowrap">{s.state}</td>
              <td className="px-1 py-0.5">{s.send_count}</td><td className="px-1 py-0.5">{s.poll_attempts}</td>
              <td className="px-1 py-0.5">{s.real_cost_usd != null ? `$${s.real_cost_usd}` : "—"}</td>
              <td className="px-1 py-0.5 font-mono">{s.response_id_short || "—"}</td><td className="px-1 py-0.5">{s.defer_reason || "—"}</td>
            </tr>))}</tbody>
        </table>
      </div>
      {r.authorization && (
        <Row label="Autorizzazione">{r.authorization.id} · {r.authorization.consumed ? `consumata ${(r.authorization.consumed_at || "").slice(0, 16)}` : (r.authorization.expired ? "scaduta" : "non consumata")} · tetto ${r.authorization.max_cost_usd}</Row>
      )}
      {(r.recovery_authorizations || []).map((x) => (
        <Row key={x.id} label="Recovery GET-only">{x.id} · {x.consumed ? "consumata" : "non consumata"} · esito {x.outcome || "—"} ({x.gets_performed ?? 0}/{x.max_gets} GET, {x.max_posts} POST)</Row>
      ))}
      {(r.owner_decisions || []).map((d, i) => (
        <Row key={i} label="Decisione Owner">{d.decision}{d.superseded_by?.logical_reader_run_id ? ` → ${d.superseded_by.logical_reader_run_id}` : ""}</Row>
      ))}
      {(r.accounting_markers || []).map((m, i) => (
        <Row key={i} label="Marker contabile">{m.marker} · esposizione incerta ${m.uncertain_exposure_usd} (diagnostico)</Row>
      ))}
      {r.authorization?.code_bindings && (
        <details className="border border-border/60"><summary className="touch-target px-2 text-[10px] cursor-pointer list-none">Hash e binding</summary>
          <div className="p-2 space-y-0.5">{Object.entries({ ...(r.authorization.frozen_hashes || {}), ...r.authorization.code_bindings }).map(([k, v]) => (
            <div key={k} className="text-[10px] min-w-0"><span className="text-muted-foreground">{k}:</span> <span className="font-mono break-anywhere">{String(v)}</span></div>))}</div>
        </details>
      )}
    </div>
  </details>
);

export const TechnicalHistory = ({ jobId, excludeRunId, children }) => {
  const [h, setH] = useState(null);
  useEffect(() => {
    if (!jobId) return;
    api.get(`/reader/jobs/${jobId}/segmented-run-fast/history`).then(({ data }) => setH(data)).catch(() => setH({ runs: [] }));
  }, [jobId]);
  const runs = (h?.runs || []).filter((r) => r.run_id !== excludeRunId);
  return (
    <details className="border border-border" data-testid="technical-history-section">
      <summary className="touch-target flex items-center justify-between gap-2 px-2 cursor-pointer select-none text-[11px] font-semibold bg-muted list-none">
        <span className="flex items-center gap-1"><History size={12} />Cronologia tecnica e run precedenti ({runs.length + (h?.legacy_p17 ? 1 : 0)})</span>
        <ChevronDown size={14} className="shrink-0" />
      </summary>
      <div className="p-2 space-y-2" data-testid="technical-history-body">
        {runs.map((r) => <RunHistoryEntry key={r.run_id} r={r} />)}
        {h?.legacy_p17 && (
          <div className="text-[10px] border border-border/70 px-2 py-1" data-testid="tech-history-legacy">
            <span className="font-mono">{h.legacy_p17.run_id}</span> · <b>{h.legacy_p17.status}</b> · {h.legacy_p17.reader_version} · legacy P1.7{h.legacy_p17.incomplete_reason ? ` · ${h.legacy_p17.incomplete_reason}` : ""}
          </div>
        )}
        {h && runs.length === 0 && !h.legacy_p17 && <div className="text-[10px] text-muted-foreground">Nessun run precedente.</div>}
        {children}
      </div>
    </details>
  );
};
